import { Modal, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { FaSave, FaTimes, FaPercent, FaBuilding, FaLock } from 'react-icons/fa';

const PorcentajeModal = ({ show, onHide, porcentaje, empresas, opcionesTipoVenta, onSave, loading }) => {
    const [formData, setFormData] = useState({
        cveCompania: '',
        tipoVenta: '',
        porcentaje: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (porcentaje) {
            // Modo edición
            setFormData({
                cveCompania: porcentaje.empresa || '',
                tipoVenta: porcentaje.tipoVenta || '',
                porcentaje: porcentaje.porcentaje?.toString() || ''
            });
        } else {
            // Modo creación
            setFormData({
                cveCompania: '',
                tipoVenta: '',
                porcentaje: ''
            });
        }
        setErrors({});
    }, [porcentaje, show]);

    const handleChange = (e) => {
        // En modo edición, no permitir cambios en empresa y tipoVenta
        if (porcentaje && (e.target.name === 'cveCompania' || e.target.name === 'tipoVenta')) {
            return;
        }
        
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};        
        if (!formData.cveCompania) {
            newErrors.cveCompania = 'La empresa es requerida';
        }        
        if (!formData.tipoVenta) {
            newErrors.tipoVenta = 'El tipo de venta es requerido';
        }        
        if (!formData.porcentaje.trim()) {
            newErrors.porcentaje = 'El porcentaje es requerido';
        } else {
            const porcentajeNum = parseFloat(formData.porcentaje);
            if (isNaN(porcentajeNum)) {
                newErrors.porcentaje = 'El porcentaje debe ser un número válido';
            } else if (porcentajeNum < 0 || porcentajeNum > 1) {
                newErrors.porcentaje = 'El porcentaje debe estar entre 0 y 1 (0% a 100%)';
            } else if (porcentajeNum.toString().split('.')[1]?.length > 4) {
                newErrors.porcentaje = 'Máximo 4 decimales';
            }
        }        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            onSave(formData);
        }
    };

    // Función para mostrar el valor en formato porcentual
    const getPorcentajeDisplay = () => {
        if (!formData.porcentaje) return '';
        const num = parseFloat(formData.porcentaje);
        if (isNaN(num)) return '';
        return `Equivale a: ${(num * 100).toFixed(2)}%`;
    };

    const isEditMode = !!porcentaje;

    // Obtener descripción de la empresa seleccionada
    const getEmpresaDescripcion = () => {
        const empresa = empresas.find(emp => emp.cveCompania === formData.cveCompania);
        return empresa ? `${empresa.cveCompania} - ${empresa.descripcion}` : formData.cveCompania;
    };

    // Obtener descripción del tipo de venta seleccionado
    const getTipoVentaDescripcion = () => {
        const tipo = opcionesTipoVenta.find(t => t.valor === formData.tipoVenta);
        return tipo ? tipo.descripcion : formData.tipoVenta;
    };

    return (
        <Modal show={show} onHide={onHide} centered backdrop="static" size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {isEditMode ? 'Editar Porcentaje de Autorización' : 'Nuevo Porcentaje de Autorización'}
                </Modal.Title>
            </Modal.Header>            
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Empresa * 
                                    {isEditMode && <FaLock className="ms-2 text-muted" size={12} />}
                                </Form.Label>
                                {isEditMode ? (
                                    // En modo edición: mostrar como texto con estilo de combo
                                    <div className="form-control bg-light">
                                        <strong>{getEmpresaDescripcion()}</strong>
                                    </div>
                                ) : (
                                    // En modo creación: combo normal
                                    <Form.Select
                                        name="cveCompania"
                                        value={formData.cveCompania}
                                        onChange={handleChange}
                                        isInvalid={!!errors.cveCompania}
                                    >
                                        <option value="">Seleccione empresa...</option>
                                        {empresas.map(empresa => (
                                            <option key={empresa.cveCompania} value={empresa.cveCompania}>
                                                {empresa.cveCompania} - {empresa.descripcion}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                                <Form.Control.Feedback type="invalid">
                                    {errors.cveCompania}
                                </Form.Control.Feedback>
                                {isEditMode && (
                                    <Form.Text className="text-muted">
                                        <FaLock className="me-1" size={10} />
                                        La empresa no puede modificarse
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>                        
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Tipo de Venta * 
                                    {isEditMode && <FaLock className="ms-2 text-muted" size={12} />}
                                </Form.Label>
                                {isEditMode ? (
                                    // En modo edición: mostrar como texto con estilo de combo
                                    <div className="form-control bg-light">
                                        <strong>{getTipoVentaDescripcion()}</strong>
                                    </div>
                                ) : (
                                    // En modo creación: combo normal
                                    <Form.Select
                                        name="tipoVenta"
                                        value={formData.tipoVenta}
                                        onChange={handleChange}
                                        isInvalid={!!errors.tipoVenta}
                                    >
                                        <option value="">Seleccione tipo...</option>
                                        {opcionesTipoVenta.map(tipo => (
                                            <option key={tipo.valor} value={tipo.valor}>
                                                {tipo.descripcion}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                                <Form.Control.Feedback type="invalid">
                                    {errors.tipoVenta}
                                </Form.Control.Feedback>
                                {isEditMode && (
                                    <Form.Text className="text-muted">
                                        <FaLock className="me-1" size={10} />
                                        El tipo de venta no puede modificarse
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={8}>
                            <Form.Group className="mb-3">
                                <Form.Label>Porcentaje (Decimal) *</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number"
                                        name="porcentaje"
                                        value={formData.porcentaje}
                                        onChange={handleChange}
                                        isInvalid={!!errors.porcentaje}
                                        placeholder="0.00"
                                        step="0.0001"
                                        min="0"
                                        max="1"
                                    />
                                    <span className="input-group-text">
                                        <FaPercent />
                                    </span>
                                </div>
                                <Form.Control.Feedback type="invalid">
                                    {errors.porcentaje}
                                </Form.Control.Feedback>
                                <Form.Text className="text-muted">
                                    {getPorcentajeDisplay()} • Rango: 0 a 1 • Ejemplo: 4% = 0.04
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                    {/* Información del registro en edición */}
                    {/* {isEditMode && (
                        <div className="alert alert-info py-2 mb-0">
                            <div className="d-flex align-items-center">
                                <FaBuilding className="me-2" />
                                <div>
                                    <small className="fw-bold">
                                        Editando porcentaje para:
                                    </small>
                                    <br />
                                    <small>
                                        <strong>{getEmpresaDescripcion()}</strong> • 
                                        <strong> {getTipoVentaDescripcion()}</strong>
                                    </small>
                                </div>
                            </div>
                        </div>
                    )} */}
                </Modal.Body>                
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={onHide}
                        disabled={loading}
                    >
                        <FaTimes className="me-2" />
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary" 
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                {isEditMode ? 'Actualizando...' : 'Creando...'}
                            </>
                        ) : (
                            <>
                                <FaSave className="me-2" />
                                {isEditMode ? 'Actualizar Porcentaje' : 'Crear Porcentaje'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default PorcentajeModal;